import type { IAssetTemplateRemoveConfirmState } from '../types';
import type { AssetTemplate } from '~/modules/shopify';

import {
  CampaignPickItemMobileActions,
  CampaignPickItemRow,
  ModalRemoveVariantConfirm,
} from '~/modules/campaign/components/CampaignPickItem';
import { useRemoveTemplateFromCampaign, useAssetTemplateItemState } from '../hooks';
import { AssetTemplateItemRenderer } from './AssetTemplateItemRenderer';
import { ModalEditExperiments } from '../../modal-edit-experiments';
import { useAssetTemplatePickerContext } from '../contexts';
import { ResourceItem, Thumbnail } from '@shopify/polaris';
import { ICampaignType } from '~/modules/campaign/types';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getExperimentUsages } from '../utils';

interface AssetTemplateItemWrapperProps {
  template: AssetTemplate;
}

export const AssetTemplateItemWrapper: React.FC<AssetTemplateItemWrapperProps> = ({ template }) => {
  const { t } = useTranslation();
  const { handleSetSelectedItem, usedTemplateSuffixes } = useAssetTemplatePickerContext();

  const [removeConfirmState, setRemoveConfirmState] =
    useState<IAssetTemplateRemoveConfirmState | null>(null);
  const [isEditExperimentsOpen, setIsEditExperimentsOpen] = useState(false);

  const { handleRemoveTemplate, isLoading } = useRemoveTemplateFromCampaign();

  // Read live rather than snapshotting on open, so removing from inside the modal drops the row.
  const experimentUsages = useMemo(
    () => getExperimentUsages(usedTemplateSuffixes[template.key]),
    [usedTemplateSuffixes, template.key],
  );

  const handleOpenRemoveConfirm = useCallback((params: IAssetTemplateRemoveConfirmState) => {
    setRemoveConfirmState(params);
  }, []);

  const handleOpenEditExperiments = useCallback(() => {
    setIsEditExperimentsOpen(true);
  }, []);

  const handleCloseEditExperiments = useCallback(() => {
    setIsEditExperimentsOpen(false);
  }, []);

  const handleCloseRemoveConfirm = useCallback(() => {
    if (isLoading) return;
    setRemoveConfirmState(null);
  }, [isLoading]);

  const handleConfirmRemove = useCallback(async () => {
    if (!removeConfirmState) return;

    const isRemoved = await handleRemoveTemplate({
      templateKey: template.key!,
      campaignId: removeConfirmState.campaignId,
      campaignType: removeConfirmState.campaignType,
      priceId: removeConfirmState.priceId,
      urlId: removeConfirmState.urlId,
    });
    if (!isRemoved) return;

    setRemoveConfirmState(null);
  }, [removeConfirmState, handleRemoveTemplate, template.key]);

  const { templateDetail, templateStatus, templateInteraction } = useAssetTemplateItemState({
    template,
    onOpenRemoveConfirm: handleOpenRemoveConfirm,
    onOpenEditExperiments: handleOpenEditExperiments,
  });

  const removeDescription = useMemo(() => {
    const name = removeConfirmState?.campaignName;

    // A price experiment holds the product the template renders for, so that is what it gives back.
    // Naming one product while removing Control clears both sides follows the template path, which
    // soft-deletes the whole campaign behind an equally plain line -- the behaviour users know.
    if (removeConfirmState?.campaignType === ICampaignType.PRICE) {
      return t('Are you sure you want to remove product from "{{name}}"?', { name });
    }

    // A URL experiment holds the page it sends traffic to, so the page is what it hands back.
    if (removeConfirmState?.campaignType === ICampaignType.URL) {
      return t('Are you sure you want to remove page from "{{name}}"?', { name });
    }

    return t('Are you sure you want to remove template from "{{name}}"?', { name });
  }, [t, removeConfirmState]);

  return (
    <>
      <CampaignPickItemRow
        interaction={templateInteraction}
        className="Polaris-GTemplate-Item Polaris-GTemplate-Item__Center"
      >
        <ResourceItem
          ariaControls="ResourceItem-Center"
          id={template.key!}
          media={
            <Thumbnail
              source={templateDetail.thumbnail || ''}
              alt={templateDetail.label || ''}
              size="medium"
            />
          }
          name={templateDetail.templateInfo.suffix}
          onClick={(id) => id && handleSetSelectedItem([id])}
          disabled={templateInteraction?.isDisabled}
          verticalAlignment="center"
        >
          <AssetTemplateItemRenderer
            detail={templateDetail}
            status={templateStatus}
            interaction={templateInteraction}
          />
        </ResourceItem>

        <CampaignPickItemMobileActions actions={templateInteraction?.shortcutActions} />
      </CampaignPickItemRow>

      {/* Every listing row mounts this wrapper, but at most one row can open the modal. Rendering it
          only then keeps its campaign queries out of the other rows. */}
      {isEditExperimentsOpen && (
        <ModalEditExperiments
          open
          usages={experimentUsages}
          onClose={handleCloseEditExperiments}
          onOpenRemoveConfirm={handleOpenRemoveConfirm}
        />
      )}

      {/* Mounted after the experiments modal so it stacks above it when both are open. */}
      <ModalRemoveVariantConfirm
        open={!!removeConfirmState}
        description={removeDescription}
        isLoading={isLoading}
        onClose={handleCloseRemoveConfirm}
        onConfirm={handleConfirmRemove}
      />
    </>
  );
};
